/**
 * JWT token payload structure
 */
export interface JwtPayload {
  exp: number;
  [key: string]: unknown;
}

/**
 * Securely decode JWT token payload with Unicode support
 * Handles URL-safe base64 and non-ASCII characters
 *
 * @param token - The JWT token string
 * @returns Decoded JWT payload
 * @throws Error if token format is invalid or decoding fails
 */
export function decodeJwtPayload(token: string): JwtPayload {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT token format');
  }

  try {
    // Handle URL-safe base64 (replace URL-safe chars with standard base64)
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');

    // Pad if necessary
    while (base64.length % 4) {
      base64 += '=';
    }

    // Decode with Unicode support
    const decoded = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(decoded) as JwtPayload;
  } catch {
    throw new Error('Failed to decode JWT token');
  }
}
